/**
 * The arithmetic behind the clearance cross-section (the Profile tab).
 *
 * Pure and DOM-free so it is unit-testable without r3f or a canvas, in the
 * same split `lib/stationVolumes/sectionModel.ts` uses: this file is the
 * numbers, `ClearanceSectionChart` is the drawing and the interaction.
 *
 * What the section shows, and why it is a grid rather than a pair of
 * surfaces: a clearance run has no surface pair. What it has is a cloud, and
 * the question the section answers is "what is actually in the envelope at
 * this station, and how far in". A per-offset min/max band cannot answer that
 * — a wire above an empty gap and a solid wall both reduce to the same two
 * numbers. An occupancy grid draws what is there.
 */
import type { ClearanceSectionBins, ClearanceVertex } from './types';

/** The two planes of `<stem>_station_clearance_section.u16`, decoded. */
export interface ClearanceSection {
  /** Scored points per cell, `nStations * nHeights * nOffsets`, C order. */
  count: Uint16Array;
  /** The violating subset of `count`, same shape and order. */
  violations: Uint16Array;
  bins: ClearanceSectionBins;
}

/**
 * Decode the raw byproduct against its OWN descriptor.
 *
 * The length check is not ceremony. The one defect that cost this feature two
 * deploy cycles was a binary payload whose declared layout disagreed with its
 * bytes (the `Clearance` extra dimension, declared 16 bytes before where it
 * actually sat), and it was silent — the reader got plausible-looking zeros.
 * A grid decoded against the wrong shape is the same failure with the same
 * symptom: a section that looks like a section and is not this station's.
 */
export function decodeClearanceSection(buf: ArrayBuffer, bins: ClearanceSectionBins): ClearanceSection {
  const per = bins.nStations * bins.nHeights * bins.nOffsets;
  const expected = per * 2 * 2; // 2 planes, uint16
  if (buf.byteLength !== expected) {
    throw new Error(
      `${bins.file} is ${buf.byteLength} bytes, expected ${expected} for ` +
        `${bins.nStations}x${bins.nHeights}x${bins.nOffsets}x2 planes of uint16`,
    );
  }
  return {
    count: new Uint16Array(buf, 0, per),
    violations: new Uint16Array(buf, per * 2, per),
    bins,
  };
}

/** One station's two planes, as views — no copy, no allocation per frame. */
export function sectionCellsAt(section: ClearanceSection, stationIndex: number) {
  const { nStations, nHeights, nOffsets } = section.bins;
  // Clamp rather than throw: the walk can sit past the last bin while the
  // dock is open on a shorter run, and a section is a view, not a
  // measurement API. (The server-side twin of this rule throws instead —
  // deliberately, see CLAUDE.md on the per-station PDF.)
  const i = Math.max(0, Math.min(nStations - 1, stationIndex));
  const a = i * nHeights * nOffsets;
  const b = a + nHeights * nOffsets;
  return { count: section.count.subarray(a, b), violations: section.violations.subarray(a, b) };
}

/** Cell centre in metres, for axis labels and hover readouts. */
export const offsetAt = (bins: ClearanceSectionBins, col: number): number =>
  bins.offsetStartM + (col + 0.5) * bins.offsetStepM;
export const heightAt = (bins: ClearanceSectionBins, row: number): number =>
  bins.heightStartM + (row + 0.5) * bins.heightStepM;

/** Inverse, for turning a pointer position back into a cell. */
export const colAt = (bins: ClearanceSectionBins, offsetM: number): number =>
  Math.floor((offsetM - bins.offsetStartM) / bins.offsetStepM);
export const rowAt = (bins: ClearanceSectionBins, heightM: number): number =>
  Math.floor((heightM - bins.heightStartM) / bins.heightStepM);

/**
 * The busiest cell in one station, used to normalise the density ramp.
 *
 * Per-station, not per-run: station-to-station density varies by an order of
 * magnitude (a scan slows at a junction, a bin clips the end of the
 * alignment), and normalising the whole run to its densest station would draw
 * every other station as an almost-empty grid.
 */
export function peakCount(cells: Uint16Array): number {
  let max = 0;
  for (let i = 0; i < cells.length; i++) if (cells[i] > max) max = cells[i];
  return max;
}

/**
 * Density → 0..1 ink, on a LOG curve.
 *
 * Linear is unreadable here: a road surface cell holds thousands of returns
 * and a thin pole a handful, so on a linear ramp the pole — the thing you are
 * looking for — is invisible next to the tarmac. `log1p` keeps the sparse
 * structure legible while the dense structure still reads as solid.
 */
export function density(value: number, peak: number): number {
  if (value <= 0 || peak <= 0) return 0;
  return Math.log1p(value) / Math.log1p(peak);
}

export interface SectionViewport {
  /** Pixels per metre. ONE scale for both axes — see below. */
  scale: number;
  originX: number;
  originY: number;
  width: number;
  height: number;
}

/**
 * Fit the grid's extent into the pane at a SINGLE scale for both axes.
 *
 * Deliberately no vertical exaggeration, which is where this parts company
 * with the station-volumes section. There, Δz is centimetres across a corridor
 * tens of metres wide, so a stated exaggeration is the only way to see
 * anything. Here both axes are the same kind of quantity at the same order of
 * magnitude (a ±3.6 m by 8 m box), and the profile's SHAPE is the thing being
 * judged — a gauge drawn with independent axes is a lie about the gauge, which
 * is exactly what `ClearanceProfileSketch` says in its own comment.
 */
/** Per-side padding, the shape `StationSectionChart`'s `MARGIN` has. */
export interface SectionMargin {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export function sectionViewport(
  bins: ClearanceSectionBins,
  widthPx: number,
  heightPx: number,
  /** One pad on every side, or per side — the axes need more room on the
   *  left (height labels) and bottom (offset labels) than on the other two. */
  pad: number | SectionMargin,
  /**
   * Where a drawing narrower than the pane sits across it. `center` (the
   * default) splits the spare width; `start` pins it to the left margin, which
   * is what the dock's section wants since it sits beside a stats panel — a
   * 1:1 gauge centred in a wide pane leaves a hole on both sides of it and
   * puts the stats a long way from the drawing they describe.
   */
  align: 'center' | 'start' = 'center',
): SectionViewport {
  const m = typeof pad === 'number' ? { top: pad, right: pad, bottom: pad, left: pad } : pad;
  const spanX = Math.max(bins.nOffsets * bins.offsetStepM, 1e-6);
  const spanY = Math.max(bins.nHeights * bins.heightStepM, 1e-6);
  const usableW = Math.max(1, widthPx - m.left - m.right);
  const usableH = Math.max(1, heightPx - m.top - m.bottom);
  const scale = Math.min(usableW / spanX, usableH / spanY);
  return {
    scale,
    // Centre (or pin) the drawing in whatever the pane left over.
    originX: m.left + (align === 'start' ? 0 : (usableW - spanX * scale) / 2),
    originY: m.top + (usableH - spanY * scale) / 2,
    width: spanX * scale,
    height: spanY * scale,
  };
}

/** Metres → pixels. Y is flipped: height grows upward, screen Y grows down. */
export const toPxX = (v: SectionViewport, bins: ClearanceSectionBins, offsetM: number): number =>
  v.originX + (offsetM - bins.offsetStartM) * v.scale;
export const toPxY = (v: SectionViewport, bins: ClearanceSectionBins, heightM: number): number =>
  v.originY + v.height - (heightM - bins.heightStartM) * v.scale;

/** The profile polygon as an SVG points string in the same viewport. */
export function profilePolygonPoints(
  vertices: readonly ClearanceVertex[],
  v: SectionViewport,
  bins: ClearanceSectionBins,
): string {
  return vertices.map(([o, h]) => `${toPxX(v, bins, o).toFixed(2)},${toPxY(v, bins, h).toFixed(2)}`).join(' ');
}

/**
 * Nice tick values covering `[start, start + span]` at a sensible step.
 *
 * Chosen from the SCALE, not from a fixed count: at 4 px/m a tick per metre is
 * a smear, and at 60 px/m a tick every 5 m is two ticks on the whole axis.
 */
export function sectionTicks(
  startM: number,
  spanM: number,
  pxPerM: number,
  /** The spacing aimed for; a short dock's section wants them closer. */
  targetPx = 56,
): number[] {
  const rawStep = targetPx / Math.max(pxPerM, 1e-6);
  const steps = [0.1, 0.2, 0.5, 1, 2, 5, 10, 20, 50];
  const step = steps.find((s) => s >= rawStep) ?? steps[steps.length - 1];
  const first = Math.ceil(startM / step) * step;
  const out: number[] = [];
  for (let v = first; v <= startM + spanM + 1e-9; v += step) {
    // Kill float drift so a tick reads "0" rather than "-1.1e-16".
    out.push(Math.abs(v) < 1e-9 ? 0 : Number(v.toFixed(6)));
  }
  return out;
}

/**
 * An offset-axis tick in signed metres — `−2`, `0`, `+1.5` — the convention
 * every other offset in the clearance UI uses (`formatSigned`: the readouts,
 * the table's Worst offset, the tooltip). The section used to say `2L / CL /
 * 2R`, the station-volumes section's labels, which made it the one place a
 * reader had to translate `+1.42 m` into "1.42 R". Zero is a bare `0`; the
 * drawing puts "CL" under it separately, so the centreline is still named.
 * Precision follows the step, as the height ticks do: whole metres stay
 * whole, a 0.5 m step keeps its decimal.
 */
export function signedOffsetTick(v: number): string {
  if (Math.abs(v) < 1e-9) return '0';
  const decimals = Number.isInteger(v) ? 0 : Math.abs(v * 10 - Math.round(v * 10)) < 1e-6 ? 1 : 2;
  const a = Math.abs(v).toFixed(decimals);
  return `${v < 0 ? '\u2212' : '+'}${a}`;
}

/** Where the worst-point label goes, and the leader line that ties it back. */
export interface WorstLabelPlacement {
  /** The text anchor point (baseline). */
  x: number;
  y: number;
  anchor: 'start' | 'end';
  /** `inline`: beside the marker, no room outside the envelope — no leader. */
  side: 'left' | 'right' | 'inline';
  /** The leader: from the marker's ring to just short of the text. */
  leader: { x1: number; y1: number; x2: number; y2: number } | null;
}

/**
 * Put the worst-point label OUTSIDE the envelope (audit §A5: it used to sit on
 * the outline, red text on the pale near-miss halo, and covered the very cells
 * it was pointing at), on the side of the gauge the point is on, past the
 * outermost near-miss band, with a leader line back to the marker.
 *
 * If that side has no room for the label inside the pane it flips to the
 * other side; if neither fits (a pane barely wider than the gauge) it falls
 * back to beside the marker, which is where it always was — a label that
 * overlaps is better than one drawn off the edge.
 *
 * `labelW` is an estimate (the SVG cannot be measured before it is drawn);
 * the caller sizes it from the string length at the tick font.
 */
export function worstLabelPlacement(p: {
  markerX: number;
  markerY: number;
  /** The envelope's left / right extremes in px, including the halo. */
  envLeftPx: number;
  envRightPx: number;
  labelW: number;
  /** The pane's drawable x-range and the plot's top / bottom. */
  minX: number;
  maxX: number;
  top: number;
  bottom: number;
}): WorstLabelPlacement {
  const GAP = 10;
  const RING = 8;
  const y = Math.max(p.top + 10, Math.min(p.bottom - 4, p.markerY));
  const centre = (p.envLeftPx + p.envRightPx) / 2;
  const rightX = p.envRightPx + GAP;
  const leftX = p.envLeftPx - GAP;
  const fitsRight = rightX + p.labelW <= p.maxX;
  const fitsLeft = leftX - p.labelW >= p.minX;
  const preferRight = p.markerX >= centre;
  let side: 'right' | 'left' | 'inline';
  if (preferRight) side = fitsRight ? 'right' : fitsLeft ? 'left' : 'inline';
  else side = fitsLeft ? 'left' : fitsRight ? 'right' : 'inline';
  if (side === 'inline') {
    const left = p.markerX + 12 + p.labelW > p.maxX;
    const x = p.markerX + (left ? -12 : 12);
    return {
      x,
      y: p.markerY - 9,
      anchor: left ? 'end' : 'start',
      side,
      leader: null,
    };
  }
  const x = side === 'right' ? rightX : leftX;
  const dir = side === 'right' ? 1 : -1;
  return {
    x,
    y,
    anchor: side === 'right' ? 'start' : 'end',
    side,
    // Text baseline sits ~3 px below the visual middle of an 11 px label, so
    // the leader ends at the label's middle rather than under it.
    leader: { x1: p.markerX + dir * RING, y1: p.markerY, x2: x - dir * 3, y2: y - 3 },
  };
}

/**
 * Signed distance from a point to the profile polygon, in metres: NEGATIVE
 * inside (an intrusion depth), positive outside (a clearance margin).
 *
 * Even-odd containment plus the nearest edge — the profile is a simple
 * polygon of at most `MAX_PROFILE_VERTICES` vertices, so the brute force is
 * the honest choice; there is nothing to index.
 */
export function profileSignedDistance(vertices: readonly ClearanceVertex[], o: number, h: number): number {
  const n = vertices.length;
  if (n < 3) return Infinity;
  let inside = false;
  let best = Infinity;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const [xi, yi] = vertices[i];
    const [xj, yj] = vertices[j];
    if (yi > h !== yj > h && o < ((xj - xi) * (h - yi)) / (yj - yi) + xi) inside = !inside;
    const dx = xj - xi;
    const dy = yj - yi;
    const len2 = dx * dx + dy * dy;
    const t = len2 > 0 ? Math.max(0, Math.min(1, ((o - xi) * dx + (h - yi) * dy) / len2)) : 0;
    const ex = xi + t * dx - o;
    const ey = yi + t * dy - h;
    best = Math.min(best, ex * ex + ey * ey);
  }
  const d = Math.sqrt(best);
  return inside ? -d : d;
}

/**
 * `profileSignedDistance` at every cell CENTRE of one station's grid, in the
 * grid's own order (`row * nOffsets + col`).
 *
 * Computed once per run, not per station: the profile is swept unchanged
 * along the alignment, so every station's grid shares the same geometry —
 * only the counts in it differ. ~26 000 cells × ≤64 edges is a few
 * milliseconds, paid when the tab opens rather than on every hover.
 */
export function profileDistanceField(bins: ClearanceSectionBins, vertices: readonly ClearanceVertex[]): Float32Array {
  const out = new Float32Array(bins.nHeights * bins.nOffsets);
  for (let row = 0; row < bins.nHeights; row++) {
    const h = heightAt(bins, row);
    for (let col = 0; col < bins.nOffsets; col++) {
      out[row * bins.nOffsets + col] = profileSignedDistance(vertices, offsetAt(bins, col), h);
    }
  }
  return out;
}

/**
 * What a cell holds, on the same K2 ladder the chart's bars and the 3D plates
 * use (`ClearanceBarKind`), plus `empty` for a cell with nothing in it.
 *
 *  - `violation` — the worker counted at least one point inside the gauge.
 *    That is the worker's own per-point verdict, never re-derived here.
 *  - `nearMiss` — points, none inside, and the cell centre lies within the
 *    run's OUTERMOST near-miss band — the same band the verdict turns
 *    MARGINAL on (`marginalBandM` in `./verdict`), so amber means one thing
 *    on every view. Approximate by up to half a cell diagonal (the grid does
 *    not keep per-point distances), which is why the tooltip says the margin
 *    is measured at the cell centre.
 *  - `clear` — points, none inside, centre beyond that band.
 *
 * Takes the band itself rather than the ladder: this runs once per cell per
 * repaint (~26 000 times), and the caller resolves the threshold once.
 */
export type SectionCellState = 'violation' | 'nearMiss' | 'clear' | 'empty';

export function sectionCellState(
  count: number,
  violations: number,
  distanceM: number,
  /** `marginalBandM(nearMissBandsM)` — the run's outermost band. */
  marginalBandM: number,
): SectionCellState {
  if (count <= 0) return 'empty';
  if (violations > 0) return 'violation';
  return distanceM <= marginalBandM ? 'nearMiss' : 'clear';
}

/**
 * The tightest band a distance falls inside — `0.1` for a centre 0.06 m
 * out, `null` beyond the outermost. A centre that lands INSIDE the gauge in a
 * cell with no violating point (the cell straddles the outline) counts as the
 * innermost band: its points are outside, and as close as they come.
 */
export function nearMissBandFor(distanceM: number, bandsM: readonly number[]): number | null {
  const sorted = [...bandsM].sort((a, b) => a - b);
  return sorted.find((b) => Math.max(0, distanceM) <= b) ?? null;
}

/** One row of the section's visually-hidden table. */
export interface SectionColumnRow {
  fromOffsetM: number;
  toOffsetM: number;
  points: number;
  violations: number;
  /** Lowest/highest occupied cell CENTRE; `null` in an empty column group. */
  lowM: number | null;
  highM: number | null;
}

/**
 * The drawing as a table: one row per group of offset columns, at most
 * `maxRows` of them.
 *
 * Columns are SUMMED into groups rather than sampled. A sampled table skips
 * columns, and the column it skips can be the one holding the only
 * violating return — exactly the fact a screen-reader user came for.
 */
export function sectionColumnRows(
  bins: ClearanceSectionBins,
  cells: { count: Uint16Array; violations: Uint16Array },
  maxRows: number,
): { stride: number; rows: SectionColumnRow[] } {
  const stride = Math.max(1, Math.ceil(bins.nOffsets / Math.max(1, maxRows)));
  const rows: SectionColumnRow[] = [];
  for (let c0 = 0; c0 < bins.nOffsets; c0 += stride) {
    const c1 = Math.min(bins.nOffsets, c0 + stride);
    let points = 0;
    let violations = 0;
    let lo = -1;
    let hi = -1;
    for (let row = 0; row < bins.nHeights; row++) {
      for (let col = c0; col < c1; col++) {
        const i = row * bins.nOffsets + col;
        const n = cells.count[i];
        if (n === 0) continue;
        points += n;
        violations += cells.violations[i];
        if (lo < 0) lo = row;
        hi = row;
      }
    }
    rows.push({
      fromOffsetM: bins.offsetStartM + c0 * bins.offsetStepM,
      toOffsetM: bins.offsetStartM + c1 * bins.offsetStepM,
      points,
      violations,
      lowM: lo < 0 ? null : heightAt(bins, lo),
      highM: hi < 0 ? null : heightAt(bins, hi),
    });
  }
  return { stride, rows };
}
